import { useState } from 'react';
import { Placeholder, Wordmark, Icon, ProductCard } from './Shared';
import { useProducts } from '../../lib/useShopify';
import { useCart } from '../../lib/useCart';

// ──────────────────────────────────────────────────
// 01 — HERO
// ──────────────────────────────────────────────────
function Hero({ onShop }) {
  return (
    <section className="hero">
      <div className="wrap">
        <div className="hero-grid">
          <div>
            <div className="hero-eyebrow">Natural · Honest · Alive</div>
            <h1>
              <em>Natural.</em><br />
              Not over-processed<br />
              or synthetic.
            </h1>
            <p className="hero-sub">
              Nothing hidden, nothing over-polished. Slow-cooked millet bowls,
              air-dried tea, and unheated honey — made the way food was made
              before food became a category.
            </p>
            <div className="hero-ctas">
              <button className="imp-btn" onClick={onShop} data-testid="hero-shop-btn">Shop Now</button>
              <button className="imp-btn outline" data-testid="hero-inside-btn">What's Inside</button>
            </div>
          </div>
          <div className="hero-visual">
            <div className="blob"><div className="blob-inner" /></div>
            <div className="hero-product">
              <Placeholder label={'hero product shot\nstacked hawelo bowls'} />
              <div className="natural-badge" style={{ top: 24, right: -10 }}>110% Natural</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────
// 02 — PROMISE TICKER
// ──────────────────────────────────────────────────
function PromiseTicker() {
  const items = [
    'No Added Sugar',
    'No Preservatives',
    'No Artificial Flavours',
    'No Palm Oil',
    'No Maltodextrin',
    'No Anti-Caking Agents',
    'No Refined Flour',
    'No Colour',
  ];
  return (
    <section className="promise-ticker" data-testid="promise-ticker" aria-label="Our promises">
      <div className="ticker-track">
        {[0, 1].map((loop) => (
          <div className="ticker-group" key={loop} aria-hidden={loop === 1}>
            {items.map((t, i) => (
              <span className="ticker-item" key={`${loop}-${i}`}>
                <Icon kind="heart" size={16} color="#E8546A" />
                <span>{t}</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────
// 03 — PILLARS
// ──────────────────────────────────────────────────
function Pillars() {
  return (
    <section className="pillars">
      <div className="wrap">
        <div className="pillar-grid">
          <div className="pillar">
            <div className="pillar-num">01 / PHILOSOPHY</div>
            <div className="pillar-title coral">Natural.</div>
            <p className="pillar-desc">Not over-processed, not polished, not filled. We keep the texture the ingredient came with.</p>
          </div>
          <div className="pillar">
            <div className="pillar-num">02 / PHILOSOPHY</div>
            <div className="pillar-title mustard">Honest.</div>
            <p className="pillar-desc">Nothing hidden on the back of the pack. The list you read is the food you eat.</p>
          </div>
          <div className="pillar">
            <div className="pillar-num">03 / PHILOSOPHY</div>
            <div className="pillar-title green">Alive.</div>
            <p className="pillar-desc">Real ingredients have edges. Real ingredients have days. Real ingredients have us.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────
// 04 — LIVE SHOPIFY RANGE
// ──────────────────────────────────────────────────
function ProductGridHome({ onOpen, goCollections }) {
  const { products, loading } = useProducts(6);
  const { addLine } = useCart();
  const picks = products.slice(0, 6);
  return (
    <section className="products-section">
      <div className="wrap">
        <div className="section-head">
          <h2>What the shelf<br />looks like.</h2>
          <p>Six products, six recipes, same standard. Start anywhere — they all share the same pantry.</p>
        </div>
        {loading ? (
          <div className="product-rail" data-testid="product-rail-skeleton">
            {[0, 1, 2].map((i) => (
              <article className="product-card" key={i}>
                <div className="p-image"><Placeholder label="loading…" /></div>
                <h3 className="product-name">&nbsp;</h3>
                <div className="product-meta"><span className="product-price">&nbsp;</span></div>
              </article>
            ))}
          </div>
        ) : (
          <div className="product-rail" data-testid="product-rail">
            {picks.map((p) => (
              <ProductCard key={p.id} product={p} onAdd={addLine} onOpen={onOpen} />
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 48 }}>
          <button className="imp-btn outline" onClick={goCollections} data-testid="see-shelf-btn">See the whole shelf →</button>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────
// 05 — WHAT'S INSIDE (ingredient showcase)
// ──────────────────────────────────────────────────
function WhatsInside() {
  const grid = [
    { icon: 'millet',  name: 'Heirloom millets',  desc: 'Foxtail, Kodo, Little, Barnyard — toasted in 40 kg lots.' },
    { icon: 'berries', name: 'Wild-grown berries',desc: 'Sun-dried strawberry & Kashmiri blueberry. No sulphites.' },
    { icon: 'nuts',    name: 'Whole nuts & seeds',desc: 'Almonds, cashews, pumpkin, sunflower — never rancid.' },
    { icon: 'heart',   name: 'Made with love',    desc: 'Recipes cooked, not assembled. That\'s the 10% you taste.' },
  ];
  return (
    <section className="inside-showcase" data-testid="whats-inside">
      <div className="wrap">
        <div className="section-head" style={{ marginBottom: 48 }}>
          <h2>What's actually<br />in the bag.</h2>
          <p>Six ingredients or fewer. Printed in plain English. We name every spice, every nut, every fruit.</p>
        </div>
        <div className="inside-grid-home">
          {grid.map((g) => (
            <div key={g.name} className="inside-cell">
              <div className="inside-icon-wrap">
                <Icon kind={g.icon} size={56} color="#1E2B8C" />
              </div>
              <h4>{g.name}</h4>
              <p>{g.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────
// 06 — WHAT YOU WON'T FIND (crossed-out promise)
// ──────────────────────────────────────────────────
function WontFind() {
  const list = [
    'Refined white sugar',
    'Maltodextrin & bulkers',
    'Palm oil',
    'Artificial flavours',
    '"Natural flavours" (the sneaky kind)',
    'Anti-caking agents',
    'Corn syrup solids',
    'Preservatives',
    'Food colours',
    'Anything we can\'t pronounce',
  ];
  return (
    <section className="wont-find" data-testid="wont-find">
      <div className="wrap">
        <div className="wf-head">
          <div className="wf-eyebrow">The list of no.</div>
          <h2 className="wf-title">What you <span className="wf-strike">won't</span> find.</h2>
          <p className="wf-sub">Every ingredient below is legal. None of them belong in food that calls itself honest. So we left them out.</p>
        </div>
        <ul className="wf-list">
          {list.map((x) => (
            <li key={x}>
              <span className="wf-x" aria-hidden="true">✕</span>
              <span className="wf-text">{x}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────
// 07 — BOWL BREAKDOWN
// ──────────────────────────────────────────────────
function BowlBreakdown() {
  return (
    <section className="bowl-section">
      <div className="wrap">
        <div className="imp-callout bowl-callout bowl-callout-2">
          <div className="bowl-copy">
            <div className="bowl-eyebrow">The Recipe</div>
            <h3>What goes into<br />your bowl.</h3>
            <p>No fillers, no flavour boosters, no cheap stand-ins. Just two things, on repeat, forever.</p>
            <button className="imp-btn">Read the sourcing notes</button>
          </div>
          <div className="bowl-chart bowl-chart-2">
            <div className="bowl-item bowl-item-big">
              <div className="icon"><Icon kind="millet" size={72} color="#F5F0E8" /></div>
              <div className="pct" style={{ color: '#D4A843' }}>100%</div>
              <div className="label">Real ingredients</div>
              <p className="bowl-note">Whole grains, whole nuts, whole fruits. Nothing synthetic, nothing hidden.</p>
            </div>
            <div className="bowl-item bowl-item-big">
              <div className="icon"><Icon kind="heart" size={72} color="#F5F0E8" /></div>
              <div className="pct" style={{ color: '#E8546A' }}>10%</div>
              <div className="label">Love</div>
              <p className="bowl-note">The part you can't measure. Recipes cooked, not assembled. That's the taste.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────
// 08 — FOUNDER NOTE
// ──────────────────────────────────────────────────
function FounderNote() {
  return (
    <section className="founder-note" data-testid="founder-note">
      <div className="wrap">
        <div className="fn-grid">
          <div className="fn-copy">
            <div className="fn-eyebrow">— A note from us</div>
            <h2 className="fn-quote">
              <span className="fn-mark">"</span>We started HAWELO because the food we grew up with
              had a <em>handwriting</em>. Industrial food doesn't. We wanted to put ours back
              on the shelf.<span className="fn-mark">"</span>
            </h2>
            <div className="fn-sig">
              <span className="fn-name">— The HAWELO kitchen</span>
              <span className="fn-role">Founders &amp; first taste-testers</span>
            </div>
          </div>
          <div className="fn-image">
            <Placeholder label={'team kitchen\nportrait'} />
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────
// 09 — TESTIMONIALS
// ──────────────────────────────────────────────────
function Testimonials() {
  const q = [
    { name: 'Anika R.', city: 'Bangalore',  body: 'Finally a muesli that doesn\'t taste like sweetened cardboard. The berries still crunch.', stars: 5 },
    { name: 'Rohan M.', city: 'Mumbai',     body: 'Read the back of the pack. Six ingredients. Six. I trust it more than my own kitchen.',  stars: 5 },
    { name: 'Priya V.', city: 'Delhi',      body: 'The turmeric tulsi tea smells like a grandmother\'s shelf. Big compliment, in case that isn\'t clear.', stars: 5 },
    { name: 'Sameer K.',city: 'Chennai',    body: 'Honey crystallises. They told me on the pack. Appreciated. Most honey brands lie about it.', stars: 5 },
  ];
  return (
    <section className="testimonials" data-testid="testimonials">
      <div className="wrap">
        <div className="section-head" style={{ marginBottom: 40 }}>
          <h2>What the eaters<br />are saying.</h2>
          <p>Real mail from real kitchens. Edited only for length.</p>
        </div>
        <div className="testimonial-grid">
          {q.map((t, i) => (
            <div className="t-card" key={i}>
              <div className="t-stars" aria-label={`${t.stars} out of 5`}>{'★'.repeat(t.stars)}</div>
              <p className="t-body">{t.body}</p>
              <div className="t-meta">
                <strong>{t.name}</strong><span> · {t.city}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────
// 10 — PRESS STRIP
// ──────────────────────────────────────────────────
function PressStrip() {
  const logos = ['VOGUE', 'FORBES', 'CONDÉ NAST', 'HINDUSTAN TIMES', 'MID-DAY', 'THE HINDU'];
  return (
    <section className="press-strip" data-testid="press-strip">
      <div className="wrap">
        <div className="press-eyebrow">As seen in</div>
        <div className="press-grid">
          {logos.map((l) => (
            <span key={l} className="press-logo">{l}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────
// 11 — FAQ
// ──────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(0);
  const faqs = [
    { q: 'How long do the packs stay fresh?',
      a: 'Each pack is good for 6 months unopened, 30 days once you open it. We cook in small batches, ship within a week of production, and stamp a real made-on date — not a best-before mystery.' },
    { q: 'Do you use any added sugar — honestly?',
      a: 'No. Sweetness in our mueslis comes from raisins, dates, or the fruit itself. The honey is raw and unfiltered. Zero cane sugar, zero syrups, zero "dehydrated cane juice" (which is cane sugar).' },
    { q: 'Are your packs recyclable?',
      a: 'The tin is reusable and recyclable. The inner liner is compostable (bio-based film). We don\'t use multi-layer plastic because you can\'t recycle it anywhere in India yet.' },
    { q: 'Do you ship pan-India?',
      a: 'Yes. Free shipping above ₹499. Orders ship within 48 hours of placement from our kitchen in Bangalore. Average delivery: 3–5 working days.' },
    { q: 'What if I don\'t like it?',
      a: 'Write to us. We\'ll refund or replace, your pick. Honest food, honest policy.' },
  ];
  return (
    <section className="faq-section" data-testid="faq">
      <div className="wrap">
        <div className="section-head" style={{ marginBottom: 40 }}>
          <h2>Questions<br />we hear often.</h2>
          <p>If something isn't answered here, write to us at hello@hawelo.com.</p>
        </div>
        <div className="faq-list">
          {faqs.map((f, i) => (
            <div className={`faq-row ${open === i ? 'open' : ''}`} key={i}>
              <button
                className="faq-q"
                onClick={() => setOpen(open === i ? -1 : i)}
                data-testid={`faq-row-${i}`}
                aria-expanded={open === i}
              >
                <span>{f.q}</span>
                <span className="faq-toggle">+</span>
              </button>
              <div className="faq-a"><p>{f.a}</p></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────
// 12 — NEWSLETTER
// ──────────────────────────────────────────────────
function Newsletter() {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  return (
    <section className="newsletter" data-testid="newsletter">
      <div className="wrap">
        <div className="nl-card">
          <div>
            <div className="nl-eyebrow">Join the honest ones</div>
            <h3 className="nl-head">Small kitchen<br />notes. Once a month.</h3>
            <p className="nl-sub">New batches, sourcing notes, recipes. No spam, no discount pressure.</p>
          </div>
          <form
            className="nl-form"
            onSubmit={(e) => { e.preventDefault(); if (email) setDone(true); }}
            data-testid="newsletter-form"
          >
            {done ? (
              <div className="nl-thanks">Welcome. Check your inbox.</div>
            ) : (
              <>
                <input
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="newsletter-email"
                />
                <button type="submit" className="imp-btn" data-testid="newsletter-submit">Subscribe</button>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────
// FOOTER
// ──────────────────────────────────────────────────
export function Footer({ onNav }) {
  return (
    <footer className="site-footer" data-testid="footer">
      <div className="wrap">
        <h2 className="footer-tagline">
          <span className="w-ha">A Truthful Habit,</span><br />
          <span className="w-we">Pure Wellness,</span><br />
          <span className="w-lo">Made With Love.</span>
        </h2>
        <div className="footer-grid">
          <div className="footer-col footer-brand">
            <Wordmark height={32} />
            <p style={{ marginTop: 16 }}>Habit · Wellness · Love. Made in small kitchens across India. Shipped when ready, never before.</p>
          </div>
          <div className="footer-col">
            <h4>Shop</h4>
            <a onClick={() => onNav('collections')}>All Products</a>
            <a onClick={() => onNav('collections')}>Muesli</a>
            <a onClick={() => onNav('collections')}>Tea</a>
            <a onClick={() => onNav('collections')}>Honey Mix</a>
          </div>
          <div className="footer-col">
            <h4>About</h4>
            <a>Our Story</a>
            <a>Sourcing</a>
            <a>Journal</a>
            <a>Contact</a>
          </div>
          <div className="footer-col">
            <h4>Follow</h4>
            <a>Instagram</a>
            <a>YouTube</a>
            <a>Newsletter</a>
            <a>Wholesale</a>
          </div>
        </div>
        <div className="footer-bottom">
          <span>www.hawelo.com</span>
          <span>Habit · Wellness · Love</span>
          <span>© 2026 HAWELO</span>
        </div>
      </div>
    </footer>
  );
}

// ──────────────────────────────────────────────────
// HOME — section assembly
// ──────────────────────────────────────────────────
export function HomePage({ onOpen, goCollections }) {
  return (
    <div className="page-view" data-testid="home-page">
      <Hero onShop={goCollections} />
      <PromiseTicker />
      <ProductGridHome onOpen={onOpen} goCollections={goCollections} />
      <WontFind />
      <BowlBreakdown />
      <FounderNote />
      <Testimonials />
      <FAQ />
      <Newsletter />
    </div>
  );
}
